import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
        return iso;
    }
}

export default function Writing() {
    const { content, loading: contentLoading, error: contentError } = usePageContent('writing');
    const [displayPosts, setDisplayPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    usePageTitle('Writing');

    // Show only Firestore posts saved from the Admin Panel
    useEffect(() => {
        if (!contentLoading) {
            setLoading(false);
            const firestorePosts = Array.isArray(content?.posts) ? content.posts : [];
            const sorted = [...firestorePosts].sort((a, b) => new Date(b.date) - new Date(a.date));
            setDisplayPosts(sorted);
        }
    }, [content, contentLoading]);

    const error = contentError;
    const heading = content?.heading || 'Writing';

    return (
        <>
            <section className="container-narrow pt-12 md:pt-28 pb-8">
                <p className="eyebrow mb-6">Writing</p>
                <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tightest text-fg">
                    {heading}
                </h1>
            </section>

            {error && <div className="container-narrow pb-6 text-sm text-yellow-600">Note: {error}</div>}

            <section className="container-narrow pb-14 md:pb-24">
                {displayPosts.length === 0 ? (
                    <p className="text-fg-muted">No posts yet.</p>
                ) : (
                    <ul className="divide-y divide-line border-y border-line">
                        {displayPosts.map((p) => (
                            <li key={p.slug}>
                                {p.source === 'substack' ? (
                                    // Substack post - link to original
                                    <a
                                        href={p.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group block py-8 transition-colors duration-300 hover:bg-elevated -mx-4 px-4 rounded-md"
                                    >
                                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                                            <span className="font-mono text-[12px] text-fg-soft">
                                                {formatDate(p.date)}
                                            </span>
                                            <span className="text-[12px] text-fg-muted">{p.readingTime} read</span>
                                        </div>
                                        <h2 className="font-display text-[22px] md:text-[28px] tracking-tight leading-snug text-fg group-hover:text-accent transition-colors">
                                            {p.title}
                                        </h2>
                                        <p
                                            className="mt-3 text-[15px] text-fg-muted max-w-prose leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: p.excerpt }}
                                        />
                                    </a>
                                ) : (
                                    // Local blog post - link to detail page
                                    <Link
                                        to={`/writing/${p.slug}`}
                                        className="group block py-8 transition-colors duration-300 hover:bg-elevated -mx-4 px-4 rounded-md"
                                    >
                                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                                            <span className="font-mono text-[12px] text-fg-soft">
                                                {formatDate(p.date)}
                                            </span>
                                            <span className="text-[12px] text-fg-muted">{p.readingTime} read</span>
                                        </div>
                                        <h2 className="font-display text-[22px] md:text-[28px] tracking-tight leading-snug text-fg group-hover:text-accent transition-colors">
                                            {p.title}
                                        </h2>
                                        <p
                                            className="mt-3 text-[15px] text-fg-muted max-w-prose leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: p.excerpt }}
                                        />
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </>
    );
}
